import { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Typography from "@mui/material/Typography";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SaveIcon from "@mui/icons-material/Save";
import Box from "@mui/material/Box";

import type { UserCrew } from "src/shared/api-types/user";
import type { ComplianceWithCoursesResponseHydrated as ComplianceWithCourses } from "src/api/compliance";
import type { ComplianceStatus } from "src/shared/api-types/certificate";
import { getComplianceWithCourses } from "src/api/compliance";
import { getCrewUsers } from "src/api/crew";
import { getDefaultSettings, saveDefaultSettings } from "src/api/settings";
import { SettingKey } from "src/shared/api-types/user";
import { useCacheRefresh } from "src/hooks/useCacheRefresh";
import useCrew from "src/hooks/useCrew";
import PositionOverviewTable from "src/components/PositionOverviewTable";
import { formatLastFirstName } from "src/services/formatters";
import { captureHandledException } from "src/services/sentry";
import CacheStatusBanner from "src/components/CacheStatusBanner";
import Button from "src/components/Button";
import DisplayContext from "src/context/DisplayContext";
import Spinner from "src/components/Spinner";
import CheckboxAutocomplete from "src/components/CheckboxAutocomplete";
import { createSelectableObject } from "src/components/Form/types";
import { takeScreenshot } from "src/utils/screenshot";

import { CachePrefix } from "src/shared/api-types/cache";
import { EmptyState } from "src/components/EmptyState";

import "./index.scss";

// Cache refresh configuration
const CACHE_REFRESH_CONFIG = {
  baseUrl: "/compliance",
  cachePrefix: CachePrefix.USER_COMPLIANCE_WITH_COURSES,
} as const;

interface PositionOverviewProps {
  match: {
    params: {
      crewId: string;
    };
  };
}

// Type definitions for position overview
interface OperationWithMetadata {
  operation_id: number | null;
  position_id: number;
  role_id: number;
  operation_name: string;
  position_name: string;
  role_name: string;
  is_position_level?: boolean;
}

interface UserRow {
  positions: [string, ...string[]]; // First is name, rest are status strings
  id: number;
  hasAnyStatus: boolean;
}

// position_name consists of a function (e.g. pilot)
// and something like "high-level operation" / "operations group" (e.g. CAT)
const functions = [
  ["pilot"],
  ["task specialist operator", "TSOP"],
  ["flight dispatch"],
  ["flight instructor", "FI"],
  ["flight examiner", "FE"],
  ["type rating examiner", "TRE"],
  ["type rating instructor", "TRI"],
  ["ground instructor", "GI"],
].map(([f, short]) => [new RegExp(`\\b(?:${f}${short ? "|" + short : ""})\\b`, "i"), f, short] as const);

const getPosFuncPrefix = (pos: string): string => {
  for (const [re, f, short] of functions) {
    if (re.test(pos)) {
      // prefix with 0 so these get shown first
      // then add short if exists, or long form, for proper sorting by function
      // then add the full position name for further SORTING beyond / ignoring function
      return `0${short || f}${pos}`;
    }
  }
  return pos;
};

const PositionOverview: FC<PositionOverviewProps> = ({ match }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar: snack } = useSnackbar();
  const { crew } = useCrew();
  const crewId = Number(match.params.crewId);
  const [positions, setPositions] = useState<OperationWithMetadata[][]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [batchData, setBatchData] = useState<Record<string, ComplianceWithCourses> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchUsers, setSearchUsers] = useState<number[]>([]);
  const [searchRoles, setSearchRoles] = useState<number[]>([]);
  const [searchPositions, setSearchPositions] = useState<number[]>([]);
  const [searchOperations, setSearchOperations] = useState<number[]>([]);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [userOptions, setUserOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [roleOptions, setRoleOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [positionOptions, setPositionOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const [operationOptions, setOperationOptions] = useState<ReturnType<typeof createSelectableObject>[]>([]);
  const searchOpRef = useRef<HTMLInputElement>(null);
  const searchRolesRef = useRef<HTMLInputElement>(null);
  const searchPositionsRef = useRef<HTMLInputElement>(null);
  const { fullscreen, toggleFullscreen } = useContext(DisplayContext);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Cache refresh functionality
  const { isRefreshing, refreshProgress, oldestCacheTime, refreshKey, dataUpdated, forceRefresh, setOldestCacheTime } =
    useCacheRefresh({
      config: CACHE_REFRESH_CONFIG,
      crewId,
    });

  useEffect(() => {
    if (!crewId) return;

    (async () => {
      try {
        // Always set loading to true when fetching data
        setLoading(true);
        setError(null);

        const isInitialLoad = refreshKey === 0;

        // Step 1: Fetch batch compliance data, users, and default settings in parallel
        const [batchResponse, usersData, defaultSettings] = await Promise.all([
          getComplianceWithCourses(crewId, true, crew?.baseIds),
          getCrewUsers(crewId),
          isInitialLoad ? getDefaultSettings(SettingKey.POSITION_OVERVIEW) : Promise.resolve(null),
        ]);

        // Set the oldest cache time from batch response
        setOldestCacheTime(batchResponse.cachedAt || null);

        // Load default filters on initial load (crew change), keep current filters on cache refresh
        if (isInitialLoad) {
          if (defaultSettings) {
            const filters = defaultSettings.value;
            if (filters.users) setSearchUsers(filters.users);
            if (filters.roles) setSearchRoles(filters.roles);
            if (filters.positions) setSearchPositions(filters.positions);
            if (filters.operations) setSearchOperations(filters.operations);
          } else {
            setSearchUsers([]);
            setSearchRoles([]);
            setSearchPositions([]);
            setSearchOperations([]);
          }
        }

        // Step 3: Build batch data structure (userId -> compliance data) from batch response
        const batchDataMap: Record<string, ComplianceWithCourses> = {};
        usersData.forEach((user: UserCrew) => {
          if (batchResponse.data[user.id]) {
            batchDataMap[user.id] = batchResponse.data[user.id];
          }
        });

        setBatchData(batchDataMap);

        // Step 4: Transform to V1 structure
        const operationMap = new Map<string, OperationWithMetadata>();
        const positionMap = new Map<
          string,
          {
            position_id: number;
            role_id: number;
            position_name: string;
            role_name: string;
            has_courses: boolean;
            position_status: ComplianceStatus | null;
          }
        >();

        // Extract all unique operations and positions from all users
        Object.values(batchDataMap).forEach((userData) => {
          userData?.relationTree?.roles?.forEach((role) => {
            role.positions?.forEach((position) => {
              // Track positions with their course info
              const posKey = `${role.id}-${position.id}`;
              if (!positionMap.has(posKey)) {
                positionMap.set(posKey, {
                  position_id: position.id,
                  role_id: role.id,
                  position_name: position.name,
                  role_name: role.name,
                  has_courses: position.courseIds && position.courseIds.length > 0,
                  position_status: position.status,
                });
              }

              position.operations?.forEach((operation) => {
                const key = `${role.id}-${position.id}-${operation.id}`;
                if (!operationMap.has(key)) {
                  operationMap.set(key, {
                    operation_id: operation.id,
                    position_id: position.id,
                    role_id: role.id,
                    operation_name: operation.name,
                    position_name: position.name,
                    role_name: role.name,
                  });
                }
              });
            });
          });
        });

        // Convert to array and sort by operation name
        const operations = Array.from(operationMap.values()).sort((a, b) =>
          a.operation_name.localeCompare(b.operation_name),
        );

        const positionsWithCourses = Array.from(positionMap.values());

        // Group operations by position_id and sort by position function
        const positionsGrouped = operations.reduce(
          (acc: Record<string, OperationWithMetadata[]>, op) => {
            if (!acc[op.position_id]) acc[op.position_id] = [];
            acc[op.position_id].push(op);
            return acc;
          },
          {} as Record<string, OperationWithMetadata[]>,
        );

        // Check if any user has the position but NO operations
        const hasUserWithPositionButNoOperations = (posId: number, roleId: number): boolean => {
          return Object.values(batchDataMap).some((userData) => {
            const role = userData?.relationTree?.roles?.find((r) => r.id === roleId);
            const position = role?.positions?.find((p) => p.id === posId);
            if (!position) return false;
            // User has position but zero operations
            return !position.operations || position.operations.length === 0;
          });
        };

        // Add position-level entry at the beginning of each position group if needed
        const positionsWithPositionStatus: Record<string, OperationWithMetadata[]> = {};

        // First, handle all positions that have operations
        Object.entries(positionsGrouped).forEach(([posId, ops]) => {
          const posInfo = positionsWithCourses.find((p) => p.position_id === Number(posId));
          const position = ops[0]; // Get metadata from first operation

          // Show position column if position has courses OR if some users have position but no operations
          const hasCourses = posInfo?.has_courses;
          const hasUsersWithNoOps = hasUserWithPositionButNoOperations(Number(posId), position.role_id);

          if (hasCourses || hasUsersWithNoOps) {
            // Add position-level status column first
            positionsWithPositionStatus[posId] = [
              {
                operation_id: null, // Null indicates this is a position-level column
                position_id: position.position_id,
                role_id: position.role_id,
                operation_name: "", // Empty operation name for position-level
                position_name: position.position_name,
                role_name: position.role_name,
                is_position_level: true,
              },
              ...ops,
            ];
          } else {
            positionsWithPositionStatus[posId] = ops;
          }
        });

        // Then, handle positions that have NO operations but have position-level courses
        positionsWithCourses.forEach((posInfo) => {
          const posId = posInfo.position_id.toString();
          if (!positionsWithPositionStatus[posId] && posInfo.has_courses) {
            // This position has courses but no operations - add it as position-only
            positionsWithPositionStatus[posId] = [
              {
                operation_id: null,
                position_id: posInfo.position_id,
                role_id: posInfo.role_id,
                operation_name: "",
                position_name: posInfo.position_name,
                role_name: posInfo.role_name,
                is_position_level: true,
              },
            ];
          }
        });

        const positionsArray = Object.values(positionsWithPositionStatus).sort(([a], [b]) =>
          getPosFuncPrefix(a?.position_name || "").localeCompare(getPosFuncPrefix(b?.position_name || "")),
        );

        // Flatten positions array to get the correct operation order for user statuses
        const flattenedOperations = positionsArray.reduce<OperationWithMetadata[]>((acc, pos) => [...acc, ...pos], []);

        // Build users array with actual user names
        const userMap = new Map(usersData.map((u: UserCrew) => [u.id, u]));

        const usersArray: UserRow[] = Object.entries(batchDataMap)
          .map(([userId, userData]): UserRow | null => {
            if (!userData?.relationTree?.roles) return null;

            // Get user name from the users info
            const userInfo = userMap.get(Number(userId));
            const userName = userInfo?.fullname || `User ${userId}`;

            // Build status array matching operations order
            const statuses: string[] = [userName]; // First element is the name

            // Create key-based lookup for faster access (operations and positions)
            const operationStatusByKey: Record<string, ComplianceStatus | null> = {};
            const positionStatusByKey: Record<string, ComplianceStatus | null> = {};

            userData.relationTree.roles?.forEach((role) => {
              role.positions?.forEach((position) => {
                // Store position-level status
                const posKey = `${role.id}-${position.id}`;
                positionStatusByKey[posKey] = position.status;

                // Store operation-level statuses
                position.operations?.forEach((operation) => {
                  const key = `${role.id}-${position.id}-${operation.id}`;
                  operationStatusByKey[key] = operation.status;
                });
              });
            });

            // Map statuses in the correct order - using flattened operations
            flattenedOperations.forEach((op) => {
              if (op.is_position_level) {
                // For position-level columns, use position status
                const posKey = `${op.role_id}-${op.position_id}`;
                statuses.push(positionStatusByKey[posKey] || "");
              } else {
                // For operation columns, use operation status
                const key = `${op.role_id}-${op.position_id}-${op.operation_id}`;
                statuses.push(operationStatusByKey[key] || "");
              }
            });

            // Check if user has at least one non-empty status
            const hasAnyStatus = statuses.slice(1).some((status) => status && status !== "");

            return {
              positions: statuses as [string, ...string[]],
              id: Number(userId),
              hasAnyStatus,
            };
          })
          .filter((x): x is UserRow => x !== null && x.hasAnyStatus) // Filter out null and users with no statuses
          .sort((a, b) => formatLastFirstName(a.positions[0]).localeCompare(formatLastFirstName(b.positions[0])));

        setPositions(positionsArray);
        setUsers(usersArray);

        // Build autocomplete options for filters
        const userAutocompleteOptions = usersArray.map((user) =>
          createSelectableObject(user.id.toString(), user.positions[0]),
        );
        setUserOptions(userAutocompleteOptions);

        // Extract unique roles from positions
        const rolesMap = new Map<number, { id: number; name: string }>();
        positionsArray.forEach((posGroup) => {
          posGroup.forEach((pos) => {
            if (!rolesMap.has(pos.role_id)) {
              rolesMap.set(pos.role_id, { id: pos.role_id, name: pos.role_name });
            }
          });
        });
        const rolesAutocompleteOptions = Array.from(rolesMap.values())
          .map((role) => createSelectableObject(role.id.toString(), role.name))
          .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName()));
        setRoleOptions(rolesAutocompleteOptions);

        // Extract unique positions from positions
        const positionsMap = new Map<number, { id: number; name: string }>();
        positionsArray.forEach((posGroup) => {
          posGroup.forEach((pos) => {
            if (!positionsMap.has(pos.position_id)) {
              positionsMap.set(pos.position_id, { id: pos.position_id, name: pos.position_name });
            }
          });
        });
        const positionsAutocompleteOptions = Array.from(positionsMap.values())
          .map((position) => createSelectableObject(position.id.toString(), position.name))
          .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName()));
        setPositionOptions(positionsAutocompleteOptions);

        // Extract unique operations from positions
        const operationsMap = new Map<number, { id: number; name: string }>();
        positionsArray.forEach((posGroup) => {
          posGroup.forEach((pos) => {
            if (pos.operation_id && !operationsMap.has(pos.operation_id)) {
              operationsMap.set(pos.operation_id, { id: pos.operation_id, name: pos.operation_name });
            }
          });
        });
        const operationsAutocompleteOptions = Array.from(operationsMap.values())
          .map((op) => createSelectableObject(op.id.toString(), op.name))
          .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName()));
        setOperationOptions(operationsAutocompleteOptions);
        setInitialLoadComplete(true);
      } catch (err) {
        captureHandledException(err, { feature: "positionOverview", action: "load", crewId });
        setError(t("failed_to_load_position_overview"));
        setInitialLoadComplete(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [crewId, crew?.baseIds, refreshKey]);

  const handleSaveSettings = async () => {
    if (!crewId) return;

    setIsSavingSettings(true);
    try {
      const filterSettings = {
        users: searchUsers,
        roles: searchRoles,
        positions: searchPositions,
        operations: searchOperations,
      };

      await saveDefaultSettings(SettingKey.POSITION_OVERVIEW, filterSettings);
      snack(t("default_filters_saved"), { variant: "success" });
    } catch (err) {
      captureHandledException(err, { feature: "positionOverview", action: "saveDefaultFilters", crewId });
      snack(t("default_filters_save_failed"), { variant: "error" });
    } finally {
      setIsSavingSettings(false);
    }
  };

  if (loading) {
    return <Spinner message={t("loading_position_overview")} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  if (!positions.length) {
    return <EmptyState />;
  }

  return (
    <div style={{ position: "relative" }}>
      {isCapturingScreenshot && <Spinner message={t("taking_screenshot")} variant="overlay" />}
      <div
        id="myDiv"
        style={{
          ...(fullscreen && {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "white",
            overflow: "auto",
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: "10px",
          }}
        >
          <CacheStatusBanner
            cachedAt={oldestCacheTime}
            isRefreshing={isRefreshing}
            refreshProgress={refreshProgress}
            dataUpdated={dataUpdated}
            onRefresh={forceRefresh}
            isCapturingScreenshot={isCapturingScreenshot}
          />
          {!isCapturingScreenshot && (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "wrap",
                alignSelf: { xs: "flex-end", md: "auto" },
              }}
            >
              <Button
                icon={<FilterAltOffIcon />}
                onClick={() => {
                  setSearchUsers([]);
                  setSearchRoles([]);
                  setSearchPositions([]);
                  setSearchOperations([]);
                }}
                disabled={
                  searchUsers.length === 0 &&
                  searchRoles.length === 0 &&
                  searchPositions.length === 0 &&
                  searchOperations.length === 0
                }
                iconSize={"large"}
                title={t("clear_all_filters")}
              />
              <Button
                icon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={isSavingSettings}
                iconSize={"large"}
                title={t("save_default_filters")}
              />
              <Button
                icon={fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                onClick={() => toggleFullscreen()}
                iconSize={"large"}
                title={t("toggle_fullscreen")}
              />
              {!fullscreen && (
                <Button
                  icon={<CameraAltIcon />}
                  onClick={() =>
                    takeScreenshot(
                      "myDiv",
                      "position_overview",
                      oldestCacheTime || undefined,
                      () => setIsCapturingScreenshot(true),
                      () => setIsCapturingScreenshot(false),
                    )
                  }
                  iconSize={"large"}
                  title={t("take_screenshot")}
                />
              )}
            </Box>
          )}
        </Box>
        <Box
          className="input-wrapper"
          sx={({ spacing }) => ({
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: "10px",
            paddingTop: spacing(1),
            paddingBottom: spacing(1),
          })}
        >
          {!isCapturingScreenshot ? (
            <>
              <CheckboxAutocomplete
                items={userOptions}
                selected={userOptions.filter((opt) => searchUsers.includes(Number(opt.getId())))}
                label={t("filter_users")}
                onChange={(value) => {
                  setSearchUsers(value.map((v) => Number(v.getId())));
                }}
              />

              <CheckboxAutocomplete
                items={roleOptions}
                selected={roleOptions.filter((opt) => searchRoles.includes(Number(opt.getId())))}
                label={t("filter_roles")}
                onChange={(value) => {
                  setSearchRoles(value.map((v) => Number(v.getId())));
                }}
              />

              <CheckboxAutocomplete
                items={positionOptions}
                selected={positionOptions.filter((opt) => searchPositions.includes(Number(opt.getId())))}
                label={t("filter_positions")}
                onChange={(value) => {
                  setSearchPositions(value.map((v) => Number(v.getId())));
                }}
              />

              <CheckboxAutocomplete
                items={operationOptions}
                selected={operationOptions.filter((opt) => searchOperations.includes(Number(opt.getId())))}
                label={t("filter_operations")}
                onChange={(value) => {
                  setSearchOperations(value.map((v) => Number(v.getId())));
                }}
              />
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: "20px",
                flexWrap: "wrap",
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              {searchRoles.length > 0 && (
                <span>
                  <strong>{t("filter_roles")}:</strong>{" "}
                  {roleOptions
                    .filter((opt) => searchRoles.includes(Number(opt.getId())))
                    .map((opt) => opt.getDisplayName())
                    .join(", ")}
                </span>
              )}
              {searchPositions.length > 0 && (
                <span>
                  <strong>{t("filter_positions")}:</strong>{" "}
                  {positionOptions
                    .filter((opt) => searchPositions.includes(Number(opt.getId())))
                    .map((opt) => opt.getDisplayName())
                    .join(", ")}
                </span>
              )}
              {searchOperations.length > 0 && (
                <span>
                  <strong>{t("filter_operations")}:</strong>{" "}
                  {operationOptions
                    .filter((opt) => searchOperations.includes(Number(opt.getId())))
                    .map((opt) => opt.getDisplayName())
                    .join(", ")}
                </span>
              )}
            </Box>
          )}
        </Box>
        {positions.length > 0 && users.length > 0 && (
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              padding: fullscreen ? 0 : undefined,
              "&::after": dataUpdated
                ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "linear-gradient(90deg, transparent, rgba(33, 150, 243, 0.95), transparent)",
                    boxShadow: "0 0 20px rgba(33, 150, 243, 0.8), 0 0 40px rgba(33, 150, 243, 0.4)",
                    animation: "sweepDown 1.8s ease-in-out",
                    pointerEvents: "none",
                    zIndex: 10000,
                    "@keyframes sweepDown": {
                      "0%": {
                        top: "0%",
                        opacity: 1,
                      },
                      "95%": {
                        top: "95%",
                        opacity: 0.8,
                      },
                      "100%": {
                        top: "95%",
                        opacity: 0,
                      },
                    },
                  }
                : {},
            }}
          >
            <PositionOverviewTable
              crewId={crewId}
              // @ts-expect-error - JS component with any types
              positions={positions}
              // @ts-expect-error - JS component with any types
              users={users}
              // @ts-expect-error - JS component expects different type
              batchComplianceData={batchData || undefined}
              searchOperations={searchOperations}
              searchRoles={searchRoles}
              searchPositions={searchPositions}
              searchOpRef={searchOpRef}
              searchRolesRef={searchRolesRef}
              searchPositionsRef={searchPositionsRef}
              // @ts-expect-error - JS component expects string but we pass number[]
              searchUsers={searchUsers}
              initialLoadComplete={initialLoadComplete}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default PositionOverview;
